import { requestWithAbort, type RequestResponse } from '@atlassian/twg-common';

// ============================================================================
// Types
// ============================================================================

export type UserNoDataResponse = {
	status: 'NO_DATA';
};

/**
 * Outcome of an individual response batch as reported by `meta.batchOutcomes`.
 *
 * - `fulfilled` — query ran successfully.
 * - `rejected`  — AGG/Flock call failed; the corresponding payload may be
 *                 partial or stale and the UI should treat it as degraded.
 * - `skipped`   — batch was gated out by the BE allowlist and was never run.
 */
export type BatchOutcome = 'fulfilled' | 'rejected' | 'skipped';

/**
 * Map of provider name → entity-type counts.
 *
 * - First-party provider keys (`confluence`, `jira`, `loom`, `atlas`, `code`)
 *   are predictable and always-supported.
 * - Third-party keys are dynamic and depend on which integrations the user
 *   has linked (e.g. `github`, `figma`, `slack`, `google-drive`). Iterate
 *   with `Object.entries` rather than hardcoding keys.
 *
 * Inner values: `number` is a real count of distinct entities, `null` means
 * the per-column query errored or the codec dropped the column. A missing
 * entity-type key means the user has no activity of that type for that
 * integration (not queried / not present).
 */
export type RelationshipsByProvider = Record<string, Record<string, number | null>>;

/**
 * Successful response from `GET /twg-api/v1/microsite/metrics/user`.
 *
 * Returned by the Flock-backed pipeline once the
 * `twg_api_microsite_user_metrics_via_flock` gate is on; the legacy
 * Socrates path (off-gate) returns a different shape that this client does
 * not currently model.
 *
 * Lookback windows: `relationshipsByProvider` and `peopleWorkedWithCount`
 * use a 365-day window (Flock queries); `relationshipsCreated180d` uses a
 * 180-day window (Socrates).
 */
/**
 * Hydrated shared-work entity returned per-collaborator in the
 * `workByCollaborators` map. The Flock-backed BE hydrates each ARI to
 * include the entity's display name, URL, third-party ID (if applicable)
 * and `__typename` so the FE does not need a follow-up smart-link
 * resolution round-trip.
 *
 * `displayName` and `url` may be null when the BE could not resolve the
 * entity (e.g. permission-denied during hydration). `thirdPartyId` is
 * present only for third-party-bridged entities.
 */
export type SharedWorkEntity = {
	ari: string;
	__typename?: string;
	displayName?: string | null;
	url?: string | null;
	thirdPartyId?: string;
};

export type UserMicrositeHeroMetricsResponse = {
	/**
	 * Up to 3 most-relevant shared, hydrated entities per collaborator.
	 * Present **only** when the FE sent the `X-Top-Collaborators` header
	 * with at least one valid ARI and the sharedWork (`slow`) batch was
	 * allowed.
	 *
	 * Check membership with `'workByCollaborators' in response` — when the
	 * header is missing, the `slow` group was excluded via `?groups`, or
	 * the batch was skipped, the field is omitted entirely (not an empty
	 * object).
	 */
	workByCollaborators?: Record<string, SharedWorkEntity[]>;
	relationshipsByProvider: RelationshipsByProvider;
	peopleWorkedWithCount: number | null;
	relationshipsCreated180d: number | string | null;
	computedAt: string;
	meta: {
		batchOutcomes: {
			relationshipsByProvider: BatchOutcome;
			peopleWorkedWith: BatchOutcome;
			relationshipsCreated180d: BatchOutcome;
		};
	};
};

/**
 * Subset of the user-metrics groups that can be requested via the
 * `?groups=` query param. The Flock endpoint partitions its work into:
 * - `fast`   — `relationshipsByProvider` (cheapest)
 * - `medium` — `peopleWorkedWithCount`
 * - `slow`   — `workByCollaborators` (most expensive; runs ~10 cypher
 *              queries plus a hydration round-trip; still requires
 *              `X-Top-Collaborators`)
 *
 * Omitting the param entirely runs all groups. Unknown values are
 * silently ignored by the BE — keep this enum in sync with the guide.
 */
export type UserMetricsGroup = 'fast' | 'medium' | 'slow';

// Alias kept for backward compat with existing imports.
export type UserMetricsResponse = UserMicrositeHeroMetricsResponse;

/**
 * Per-provider rollup for third-party integrations only. Each entry sums
 * every numeric count for that provider into a single `events180d` figure
 * so callers can render "top providers" without an extra pass.
 */
export type Provider3pEventsByProvider = {
	providerName: string;
	events180d: number;
};

/**
 * Per-provider rollup that includes both first-party Atlassian properties
 * (`confluence`, `jira`, `loom`, `atlas`, `code`) and third-party
 * integrations. Used by surfaces that want to render every provider the
 * user has activity in — not just the dynamic third-party ones.
 */
export type ProviderEventsByProvider = {
	providerName: string;
	events180d: number;
};

// ============================================================================
// Type Guards
// ============================================================================

export const isUserNoDataResponse = (
	metrics: UserMicrositeHeroMetricsResponse | UserNoDataResponse | unknown,
): metrics is UserNoDataResponse =>
	metrics != null &&
	typeof metrics === 'object' &&
	(metrics as { status?: string }).status === 'NO_DATA';

// ============================================================================
// Constants
// ============================================================================

/**
 * Provider keys whose presence and entity-type vocabulary are guaranteed by
 * the BE. Anything not in this set is treated as a third-party integration
 * for the purposes of `provider3p…` derivations below.
 */
const FIRST_PARTY_PROVIDERS: ReadonlySet<string> = new Set([
	'confluence',
	'jira',
	'loom',
	'atlas',
	'code',
]);

/**
 * Spec cap on `X-Top-Collaborators` header — the BE will reject anything
 * over five entries, so we trim defensively client-side.
 */
const MAX_TOP_COLLABORATORS = 5;

// ============================================================================
// Request Utility
// ============================================================================

/**
 * Issue a GET to the Flock-backed user-metrics endpoint.
 *
 * @param orgId — required, scopes the metrics to an organisation.
 * @param topCollaboratorAris — optional collaborator ARIs (e.g. from the
 *   collaboration-graph). When provided, the first five are forwarded as
 *   the `X-Top-Collaborators` header so the BE includes the optional
 *   `workByCollaborators` field in the response. Each ARI must look like
 *   `ari:cloud:identity::user/...`; callers are responsible for filtering
 *   out invalid entries before calling.
 *
 * 200 and 404 are both treated as "successful" responses (404 carries the
 * `{ status: 'NO_DATA' }` sentinel — see `isUserNoDataResponse`).
 */
export const requestUserMetrics = (
	orgId: string,
	cloudId: string,
	topCollaboratorAris?: ReadonlyArray<string>,
	groups?: ReadonlyArray<UserMetricsGroup>,
): {
	controller: AbortController;
	execute: Promise<RequestResponse<UserMicrositeHeroMetricsResponse | UserNoDataResponse>>;
} => {
	const headers: Record<string, string> = {
		'X-Query-Context': `ari:cloud:platform::site/${cloudId}`,
	};
	if (topCollaboratorAris && topCollaboratorAris.length > 0) {
		headers['X-Top-Collaborators'] = topCollaboratorAris.slice(0, MAX_TOP_COLLABORATORS).join(',');
	}

	// Append `?groups=…` so the BE can short-circuit the expensive batches
	// the caller doesn't need yet. Per the integration guide, omitting
	// the param entirely runs all groups; unknown values are silently
	// ignored. Dedupe + sort for a stable URL (helps caching layers).
	const groupsParam =
		groups && groups.length > 0 ? `&groups=${Array.from(new Set(groups)).sort().join(',')}` : '';

	const { controller, execute } = requestWithAbort<
		UserMicrositeHeroMetricsResponse | UserNoDataResponse
	>(
		'GET',
		`/gateway/api/twg-api/v1/microsite/metrics/user?organisationId=${orgId}${groupsParam}`,
		null,
		headers,
		[200, 404],
	);
	return { controller, execute };
};

// ============================================================================
// Metric Selection
// ============================================================================

export const selectUserMetrics = (
	metrics: UserMicrositeHeroMetricsResponse | UserNoDataResponse | null,
): UserMicrositeHeroMetricsResponse | null => {
	if (metrics == null || isUserNoDataResponse(metrics)) {
		return null;
	}
	return metrics;
};

// ============================================================================
// Derivation Helpers
// ============================================================================
// The Flock-backed endpoint no longer returns hand-rolled top-level counts
// (pagesCreated180d, commentsCreated180d, …). Per the integration guide, the
// FE derives them from `relationshipsByProvider`. The helpers below are the
// canonical derivation surface — UI code should never reach into the raw
// `relationshipsByProvider` map directly.

const getProviderEntityCount = (
	rbp: RelationshipsByProvider,
	provider: string,
	entityType: string,
): number => {
	const value = rbp[provider]?.[entityType];
	return typeof value === 'number' ? value : 0;
};

/**
 * Sum a single entity type across every third-party provider in the
 * response. Used for headline counts (designs, pull requests, …) that
 * have no first-party equivalent.
 */
const sumThirdPartyEntityType = (rbp: RelationshipsByProvider, entityType: string): number => {
	let total = 0;
	for (const [provider, entityTypes] of Object.entries(rbp)) {
		if (FIRST_PARTY_PROVIDERS.has(provider)) {
			continue;
		}
		const value = entityTypes[entityType];
		if (typeof value === 'number') {
			total += value;
		}
	}
	return total;
};

/** Sum of `page` counts across every provider (1p + 3p, e.g. confluence + google-drive). */
export const getPagesCreated = (rbp: RelationshipsByProvider): number => {
	let total = 0;
	for (const entityTypes of Object.values(rbp)) {
		const value = entityTypes.page;
		if (typeof value === 'number') {
			total += value;
		}
	}
	return total;
};

export const getWorkItemsCreated = (rbp: RelationshipsByProvider): number =>
	getProviderEntityCount(rbp, 'jira', 'workItem');

export const getVideosCreated = (rbp: RelationshipsByProvider): number =>
	getProviderEntityCount(rbp, 'loom', 'video');

/** Sum of `comment` counts across every provider (1p + 3p). */
export const getCommentsCreated = (rbp: RelationshipsByProvider): number => {
	let total = 0;
	for (const entityTypes of Object.values(rbp)) {
		const value = entityTypes.comment;
		if (typeof value === 'number') {
			total += value;
		}
	}
	return total;
};

/** Sum of `design` entities across third-party providers (e.g. Figma). */
export const getDesignsCreated = (rbp: RelationshipsByProvider): number =>
	sumThirdPartyEntityType(rbp, 'design');

/** Sum of `pullRequest` entities across third-party providers (GitHub, GitLab, …). */
export const getPullRequestsCreated = (rbp: RelationshipsByProvider): number =>
	sumThirdPartyEntityType(rbp, 'pullRequest');

/** Releases tracked in Jira. */
export const getReleasesCreated = (rbp: RelationshipsByProvider): number =>
	getProviderEntityCount(rbp, 'jira', 'release');

/** Whiteboards in Confluence. */
export const getWhiteboardsCreated = (rbp: RelationshipsByProvider): number =>
	getProviderEntityCount(rbp, 'confluence', 'whiteboard');

/** Databases in Confluence. */
export const getDatabasesCreated = (rbp: RelationshipsByProvider): number =>
	getProviderEntityCount(rbp, 'confluence', 'database');

/** Spaces in Confluence. */
export const getSpacesCreated = (rbp: RelationshipsByProvider): number =>
	getProviderEntityCount(rbp, 'confluence', 'space');

/** Goals tracked in Atlas. */
export const getGoalsCreated = (rbp: RelationshipsByProvider): number =>
	getProviderEntityCount(rbp, 'atlas', 'goal');

/** Projects tracked in Atlas. */
export const getProjectsCreated = (rbp: RelationshipsByProvider): number =>
	getProviderEntityCount(rbp, 'atlas', 'project');

/** Focus areas tracked in Atlas. */
export const getFocusAreasCreated = (rbp: RelationshipsByProvider): number =>
	getProviderEntityCount(rbp, 'atlas', 'focusArea');

/** Goal updates tracked in Atlas. */
export const getGoalUpdatesCreated = (rbp: RelationshipsByProvider): number =>
	getProviderEntityCount(rbp, 'atlas', 'goalUpdate');

/** Project updates tracked in Atlas. */
export const getProjectUpdatesCreated = (rbp: RelationshipsByProvider): number =>
	getProviderEntityCount(rbp, 'atlas', 'projectUpdate');

/** Sum of `message` entities across every provider (e.g. Slack, MS Teams). */
export const getMessagesCreated = (rbp: RelationshipsByProvider): number => {
	let total = 0;
	for (const entityTypes of Object.values(rbp)) {
		const value = entityTypes.message;
		if (typeof value === 'number') {
			total += value;
		}
	}
	return total;
};

/** Sum of `branch` entities across third-party code providers (GitHub, GitLab, …). */
export const getBranchesCreated = (rbp: RelationshipsByProvider): number =>
	sumThirdPartyEntityType(rbp, 'branch');

/** Sum of `commit` entities across third-party code providers (GitHub, GitLab, …). */
export const getCommitsCreated = (rbp: RelationshipsByProvider): number =>
	sumThirdPartyEntityType(rbp, 'commit');

/**
 * Total contributions: sum of every numeric count across every
 * provider/entity-type pair. `null` columns are skipped so a single batch
 * failure does not poison the headline number.
 */
export const getTotalContributions = (rbp: RelationshipsByProvider): number => {
	let total = 0;
	for (const entityTypes of Object.values(rbp)) {
		for (const value of Object.values(entityTypes)) {
			if (typeof value === 'number') {
				total += value;
			}
		}
	}
	return total;
};

/**
 * Per-provider rollup for third-party integrations only. Sorted by
 * descending `events180d` so callers can render a "top providers" list
 * without an extra pass. Providers whose batches errored or returned no
 * activity are filtered out.
 */
export const getThirdPartyProviders = (
	rbp: RelationshipsByProvider,
): Provider3pEventsByProvider[] => {
	const providers: Provider3pEventsByProvider[] = [];
	for (const [providerName, entityTypes] of Object.entries(rbp)) {
		if (FIRST_PARTY_PROVIDERS.has(providerName)) {
			continue;
		}
		let events180d = 0;
		for (const value of Object.values(entityTypes)) {
			if (typeof value === 'number') {
				events180d += value;
			}
		}
		if (events180d > 0) {
			providers.push({ providerName, events180d });
		}
	}
	providers.sort((a, b) => b.events180d - a.events180d);
	return providers;
};

/** Sum of every numeric count across all third-party providers. */
export const getThirdPartyEventTotal = (rbp: RelationshipsByProvider): number =>
	getThirdPartyProviders(rbp).reduce((sum, p) => sum + p.events180d, 0);

/**
 * Per-provider rollup across **every** provider in the response — both
 * first-party Atlassian properties and third-party integrations. Each entry
 * sums every numeric entity-type count for the provider into a single
 * `events180d` figure. Sorted by descending `events180d`. Providers whose
 * batches errored or returned no activity are filtered out.
 *
 * Use this when the UI wants to render the user's full integration
 * footprint (e.g. the connected-apps bar list); use
 * {@link getThirdPartyProviders} when only third-party integrations are
 * relevant.
 */
export const getAllProviders = (rbp: RelationshipsByProvider): ProviderEventsByProvider[] => {
	const providers: ProviderEventsByProvider[] = [];
	for (const [providerName, entityTypes] of Object.entries(rbp)) {
		let events180d = 0;
		for (const value of Object.values(entityTypes)) {
			if (typeof value === 'number') {
				events180d += value;
			}
		}
		if (events180d > 0) {
			providers.push({ providerName, events180d });
		}
	}
	providers.sort((a, b) => b.events180d - a.events180d);
	return providers;
};

/**
 * Distinct count of third-party providers that contributed at least one
 * numeric count (matches the filter in `getThirdPartyProviders`).
 */
export const getThirdPartyDistinctCount = (rbp: RelationshipsByProvider): number =>
	getThirdPartyProviders(rbp).length;

export const getRelationshipsCreated180d = (
	value: UserMicrositeHeroMetricsResponse['relationshipsCreated180d'] | undefined,
): number | null => {
	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : null;
	}

	if (typeof value === 'string') {
		const trimmedValue = value.trim();
		if (!trimmedValue) {
			return null;
		}
		const parsedValue = Number(trimmedValue);
		return Number.isFinite(parsedValue) ? parsedValue : null;
	}

	return null;
};

// ============================================================================
// Tier 1 Gating
// ============================================================================

const ACCOUNT_MATURITY_DAYS = 30;

const USER_METRIC_COVERAGE_FLOORS: ReadonlyArray<{
	label: string;
	floor: number;
	derive: (response: UserMicrositeHeroMetricsResponse) => number | null;
}> = [
	{
		label: 'pagesCreated',
		floor: 10,
		derive: (r) => getPagesCreated(r.relationshipsByProvider),
	},
	{
		label: 'commentsCreated',
		floor: 30,
		derive: (r) => getCommentsCreated(r.relationshipsByProvider),
	},
	{
		label: 'workItemsCreated',
		floor: 20,
		derive: (r) => getWorkItemsCreated(r.relationshipsByProvider),
	},
	{
		label: 'videosCreated',
		floor: 23,
		derive: (r) => getVideosCreated(r.relationshipsByProvider),
	},
	{
		label: 'designsCreated',
		floor: 5,
		derive: (r) => getDesignsCreated(r.relationshipsByProvider),
	},
	{
		label: 'prsCreated',
		floor: 16,
		derive: (r) => getPullRequestsCreated(r.relationshipsByProvider),
	},
	{
		label: 'relationshipsCreated180d',
		floor: 1,
		derive: (r) => getRelationshipsCreated180d(r.relationshipsCreated180d),
	},
	{
		label: 'totalItemsCreated',
		floor: 1,
		derive: (r) => getTotalContributions(r.relationshipsByProvider),
	},
	{
		label: 'thirdPartyEventsTotal',
		floor: 1,
		derive: (r) => getThirdPartyEventTotal(r.relationshipsByProvider),
	},
	{
		label: 'thirdPartyDistinctCount',
		floor: 1,
		derive: (r) => getThirdPartyDistinctCount(r.relationshipsByProvider),
	},
];

const MIN_COVERAGE = 5;

/**
 * Evaluate whether a user qualifies for Tier 1 (user-level metrics view).
 *
 * Gate 1: Account must be >= 30 days old.
 * Gate 2: >= 5 derived user metrics individually pass their per-metric floors.
 *
 * Returns false if:
 * - metrics is null or NO_DATA
 * - accountCreatedAt is empty or unparseable (fail safe: cascade to Tier 2)
 * - account is < 30 days old
 * - fewer than 5 derived metrics pass their floors
 */
export const evaluateUserTier = (
	metrics: UserMicrositeHeroMetricsResponse | UserNoDataResponse | null,
	accountCreatedAt: string,
): boolean => {
	const userMetrics = selectUserMetrics(metrics);
	if (!userMetrics) {
		return false;
	}

	// Gate 1: account maturity — fail safe if date is missing or unparseable
	if (!accountCreatedAt) {
		return false;
	}
	const createdAtMs = Date.parse(accountCreatedAt);
	if (!Number.isFinite(createdAtMs)) {
		return false;
	}
	const ageDays = (Date.now() - createdAtMs) / (1000 * 60 * 60 * 24);
	if (ageDays < ACCOUNT_MATURITY_DAYS) {
		return false;
	}

	// Gate 2: metric coverage
	const passingMetricCount = USER_METRIC_COVERAGE_FLOORS.filter(({ derive, floor }) => {
		const value = derive(userMetrics);
		return typeof value === 'number' && value >= floor;
	}).length;

	return passingMetricCount >= MIN_COVERAGE;
};
